import type { ResponseProjection } from '@/types/mcpTool';
import type { CopilotInitializeResponse, CopilotToolRegistry } from '@/types/copilot';

export interface CopilotInitializationCache {
  response: CopilotInitializeResponse;
  conversationId: string | null;
  ctxVersion: string | null;
  toolRegistry: CopilotToolRegistry | null;
  pageContext: Record<string, any> | null;
  streamToken: string | null;
  streamTokenExpiresAt: string | null;
}

export const INITIALIZATION_CACHE_STORAGE_KEY = 'mcp_copilot_initialization_cache_v3';

let inMemoryCache: CopilotInitializationCache | null = null;

export const sanitizeConversationId = (value?: string | null): string | null => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const buildToolContextMap = (
  registry: CopilotToolRegistry | null | undefined
): Map<string, ResponseProjection> => {
  const newToolContextMap = new Map<string, ResponseProjection>();

  if (!registry?.tools || !Array.isArray(registry.tools)) {
    return newToolContextMap;
  }

  for (const tool of registry.tools) {
    if (!tool) continue;
    const toolName = tool.name || tool.tool_name;
    if (!toolName || !Array.isArray(tool.actions)) continue;

    for (const action of tool.actions) {
      if (!action) continue;
      const actionName = action.action_name || action.name;
      if (!actionName || !action.response_projection) continue;

      const key = `${toolName}.${actionName}`;
      newToolContextMap.set(key, action.response_projection);
    }
  }

  return newToolContextMap;
};

const loadInitializationCacheFromStorage = (): CopilotInitializationCache | null => {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.sessionStorage.getItem(INITIALIZATION_CACHE_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return null;

    const sanitizedConversationId = sanitizeConversationId(parsed.conversationId ?? null);
    const ctxVersion = typeof parsed.ctxVersion === 'string' ? parsed.ctxVersion : null;
    const toolRegistry = parsed.toolRegistry || null;
    const pageContext = parsed.pageContext || null;
    const response = parsed.response;
    const streamToken =
      typeof parsed.streamToken === 'string'
        ? parsed.streamToken
        : typeof parsed.stream_token === 'string'
          ? parsed.stream_token
          : null;
    const streamTokenExpiresAt =
      typeof parsed.streamTokenExpiresAt === 'string'
        ? parsed.streamTokenExpiresAt
        : typeof parsed.stream_token_expires_at === 'string'
          ? parsed.stream_token_expires_at
          : null;

    if (!response || typeof response !== 'object') {
      return null;
    }

    return {
      response,
      conversationId: sanitizedConversationId,
      ctxVersion,
      toolRegistry,
      pageContext,
      streamToken,
      streamTokenExpiresAt,
    };
  } catch (error) {
    console.error('Failed to load copilot initialization cache from session storage:', error);
    return null;
  }
};

const saveInitializationCacheToStorage = (cache: CopilotInitializationCache | null) => {
  if (typeof window === 'undefined') return;

  try {
    if (!cache) {
      window.sessionStorage.removeItem(INITIALIZATION_CACHE_STORAGE_KEY);
    } else {
      const serialized = JSON.stringify(cache);
      window.sessionStorage.setItem(INITIALIZATION_CACHE_STORAGE_KEY, serialized);
    }
  } catch (error) {
    console.error('Failed to save copilot initialization cache to session storage:', error);
  }
};

export const getInitializationCache = (): CopilotInitializationCache | null => {
  return inMemoryCache;
};

export const ensureInitializationCache = (): CopilotInitializationCache | null => {
  if (!inMemoryCache) {
    inMemoryCache = loadInitializationCacheFromStorage();
  }
  return inMemoryCache;
};

export const setInitializationCache = (cache: CopilotInitializationCache | null) => {
  if (!cache) {
    inMemoryCache = null;
    saveInitializationCacheToStorage(null);
    return;
  }

  const normalizedConversationId = sanitizeConversationId(cache.conversationId);
  const normalizedCtxVersion = cache.ctxVersion ?? null;
  const normalizedToolRegistry = cache.toolRegistry ?? null;
  const normalizedPageContext = cache.pageContext ?? null;
  const normalizedStreamToken = cache.streamToken ?? null;
  const normalizedStreamTokenExpiresAt = cache.streamTokenExpiresAt ?? null;

  const normalizedResponse: CopilotInitializeResponse = {
    ...cache.response,
    conversation_id: normalizedConversationId ?? undefined,
    ctx_version: normalizedCtxVersion ?? undefined,
    tool_registry: normalizedToolRegistry ?? null,
    page_context: normalizedPageContext ?? null,
    stream_token: normalizedStreamToken ?? undefined,
    stream_token_expires_at: normalizedStreamTokenExpiresAt ?? undefined,
  };

  const normalizedCache: CopilotInitializationCache = {
    response: normalizedResponse,
    conversationId: normalizedConversationId,
    ctxVersion: normalizedCtxVersion,
    toolRegistry: normalizedToolRegistry,
    pageContext: normalizedPageContext,
    streamToken: normalizedStreamToken,
    streamTokenExpiresAt: normalizedStreamTokenExpiresAt,
  };

  inMemoryCache = normalizedCache;
  saveInitializationCacheToStorage(normalizedCache);
};

export const clearInitializationCache = () => {
  setInitializationCache(null);
};
